import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    FaMoon,
    FaSun,
    FaBars,
    FaTimes,
    FaFileAlt,
    FaHistory,
    FaHome,
    FaProjectDiagram,
    FaTools,
    FaBriefcase,
    FaEnvelope,
    FaHeart
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';

const Navbar = ({ darkMode, toggleDarkMode, onOpenHobbies }) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { t } = useTranslation();

    const navLinks = [
        { id: 'home', name: t('navbar.home'), icon: <FaHome className="mr-1" /> },
        { id: 'projects', name: t('navbar.projects'), icon: <FaProjectDiagram className="mr-1" /> },
        { id: 'skills', name: t('navbar.skills'), icon: <FaTools className="mr-1" /> },
        { id: 'timeline', name: t('timeline.title', 'Journey'), icon: <FaHistory className="mr-1" /> },
        { id: 'experience', name: t('navbar.experience'), icon: <FaBriefcase className="mr-1" /> },
        { id: 'resume', name: t('navbar.resume'), icon: <FaFileAlt className="mr-1" /> },
        { id: 'contact', name: t('navbar.contact'), icon: <FaEnvelope className="mr-1" /> },
    ];

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 20) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setIsMobileMenuOpen(false);
        }
    };

    return (
        <motion.nav
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5 }}
            className={`fixed top-0 left-0 right-0 z-50 ${
                isScrolled
                    ? darkMode
                        ? 'bg-gray-900 shadow-lg shadow-cyan-500/10'
                        : 'bg-white shadow-lg'
                    : 'bg-transparent'
            } transition-all duration-300`}
        >
            <div className="container mx-auto px-4">
                <div className="flex items-center justify-between h-16">
                    {/* Logo / Nombre */}
                    <div className="flex-shrink-0">
                        <button
                            onClick={() => scrollToSection('home')}
                            className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}
                        >
                            <span className={darkMode ? 'text-cyan-400' : 'text-cyan-600'}>Starlin</span> Frías
                        </button>
                    </div>

                    {/* Enlaces de navegación (Desktop) */}
                    <div className="hidden md:block">
                        <div className="flex items-center space-x-4">
                            {navLinks.map(link => (
                                <button
                                    key={link.id}
                                    onClick={() => scrollToSection(link.id)}
                                    className={`px-3 py-2 rounded-md text-sm font-medium flex items-center ${
                                        darkMode
                                            ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                                    } transition-colors`}
                                >
                                    {link.icon}
                                    {link.name}
                                </button>
                            ))}

                            {/* Botón para Hobbies */}
                            <button
                                onClick={onOpenHobbies}
                                className={`px-3 py-2 rounded-md text-sm font-medium flex items-center ${
                                    darkMode
                                        ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                                } transition-colors`}
                            >
                                <FaHeart className="mr-1 text-red-500" />
                                {t('hobbies.menuTitle', 'Intereses')}
                            </button>

                            {/* Selector de idioma */}
                            <LanguageSelector darkMode={darkMode} />

                            {/* Botón de tema */}
                            <button
                                onClick={toggleDarkMode}
                                className={`p-2 rounded-full ${
                                    darkMode
                                        ? 'bg-gray-700 text-yellow-300 hover:bg-gray-600'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                } transition-colors`}
                                aria-label="Toggle dark mode"
                            >
                                {darkMode ? <FaSun /> : <FaMoon />}
                            </button>
                        </div>
                    </div>

                    {/* Botón de menú móvil */}
                    <div className="md:hidden flex items-center">
                        {/* Selector de idioma móvil */}
                        <LanguageSelector darkMode={darkMode} />

                        <button
                            onClick={toggleDarkMode}
                            className={`p-2 mx-2 rounded-full ${
                                darkMode
                                    ? 'bg-gray-700 text-yellow-300 hover:bg-gray-600'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            } transition-colors`}
                            aria-label="Toggle dark mode"
                        >
                            {darkMode ? <FaSun /> : <FaMoon />}
                        </button>

                        <button
                            onClick={toggleMobileMenu}
                            className={`p-2 rounded-md ${
                                darkMode
                                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                            aria-label="Toggle mobile menu"
                        >
                            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Menú móvil */}
            {isMobileMenuOpen && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`md:hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
                >
                    <div className="px-2 pt-2 pb-3 space-y-1">
                        {navLinks.map(link => (
                            <button
                                key={link.id}
                                onClick={() => scrollToSection(link.id)}
                                className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium flex items-center ${
                                    darkMode
                                        ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                                } transition-colors`}
                            >
                                {link.icon}
                                {link.name}
                            </button>
                        ))}

                        {/* Botón para Hobbies en móvil */}
                        <button
                            onClick={() => {
                                onOpenHobbies();
                                setIsMobileMenuOpen(false);
                            }}
                            className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium flex items-center ${
                                darkMode
                                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                            } transition-colors`}
                        >
                            <FaHeart className="mr-1 text-red-500" />
                            {t('navbar.hobbies', 'Interest')}
                        </button>
                    </div>
                </motion.div>
            )}
        </motion.nav>
    );
};

export default Navbar;